/**
 * Fallback cards rendered by Sentry.ErrorBoundary at three depths:
 *  - TopLevelErrorFallback: catastrophic — provider chain or top of App crashed.
 *  - AdminRouteErrorFallback: an admin route crashed; sidebar chrome survives.
 *  - CandidateRouteErrorFallback: a candidate route crashed; softer copy
 *    because the candidate isn't a power user and their work is the priority.
 *
 * Sentry has already captured the underlying error before any of these render;
 * the fallback just gives the user a path forward (reload).
 */

import { AlertTriangle } from "lucide-react";

import { ReportProblemTrigger } from "@/components/candidate/ReportProblemDialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { loadAttemptId, loadSubmittedAttemptId } from "@/lib/api";

function reloadPage(): void {
  window.location.reload();
}

export function TopLevelErrorFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md space-y-4">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription>
            The page failed to load. Reloading usually fixes it. If it keeps
            happening, get in touch.
          </AlertDescription>
        </Alert>
        <Button onClick={reloadPage} variant="default" className="w-full">
          Reload page
        </Button>
      </div>
    </div>
  );
}

export function AdminRouteErrorFallback() {
  return (
    <div className="px-6 py-10 md:px-10">
      <div className="mx-auto max-w-2xl space-y-4">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Couldn&rsquo;t load this page</AlertTitle>
          <AlertDescription>
            Something broke while rendering this view. Reloading should restore
            it. Other pages in the dashboard still work.
          </AlertDescription>
        </Alert>
        <Button onClick={reloadPage} variant="default">
          Reload
        </Button>
      </div>
    </div>
  );
}

/**
 * `token` is the candidate link's token (`/t/:token/...`), read off the
 * pathname by the layout that owns this boundary.
 *
 * night-p5 (2026-10-08): "Report a problem" used to live only in the Review /
 * Section-Review footers and on Done — INSIDE the subtree this boundary
 * replaces — so the one screen where a candidate most needs it was the one
 * without it. The trigger is self-contained (its own modal, its own request,
 * never touches answers), so it is rendered here too, against the attempt
 * this tab knows: the in-flight id, or the submitted one when the crash hit
 * the Done screen. No attempt in this tab (a crash before /start) → the
 * trigger renders nothing, as everywhere else — there is no row to attach a
 * report to.
 */
export function CandidateRouteErrorFallback({ token }: { token: string | null }) {
  const attemptId = token
    ? (loadAttemptId(token) ?? loadSubmittedAttemptId(token))
    : null;
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md space-y-4">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>We hit a problem</AlertTitle>
          <AlertDescription>
            Your work is saved on our servers. Please refresh the page and
            continue from where you left off.
          </AlertDescription>
        </Alert>
        <Button onClick={reloadPage} variant="default" className="w-full">
          Refresh
        </Button>
        {token && (
          <div className="flex justify-center">
            <ReportProblemTrigger token={token} attemptId={attemptId} />
          </div>
        )}
      </div>
    </div>
  );
}
